<script setup lang="ts">
import { useI18n } from '@/plugins/i18n';
import { useSearchStore } from '../../store/index.ts';
import type { SearchFilterOption } from '../../types.ts';
import FilterDropdown from '../../FilterDropdown.ce.vue';
import TimeRangeFilter from '../Updates/TimeRange/index.ce.vue';

import iconStatus from '@/assets/img/icon-search-all-filters-status.svg';
import iconPublisher from '@/assets/img/icon-search-all-filters-publisher.svg';
import iconDataset from '@/assets/img/icon-search-all-filters-dataset.svg';

import iconMain from '@/assets/img/icon-search-all-filters-year.svg';
import iconOpenMain from '@/assets/img/icon-search-all-filters-year.svg';
import iconOpen from '@/assets/img/icon-search-all-filters-open.svg';
import iconClose from '@/assets/img/icon-search-all-filters-close.svg';
import { STANDARDS_SEARCH_TAB, UPDATES_SEARCH_TAB, DATE_FORMAT } from '@/components/Search/constants.ts';
import { computed } from 'vue';
import { parse, isValid } from 'date-fns';
import { getYearDateRange } from '@/utils/dateHelpers.ts';

const props = defineProps({
    searchOnSelect: { type: Boolean, default: true }
})


//store
const searchStore = useSearchStore()

//translations
const i18nInstance: any = useI18n()
const { t } = i18nInstance

const { start: oneYearAgo, end: today } = getYearDateRange();

const startDate = computed(() => {
  let dateStart: any = searchStore[`${UPDATES_SEARCH_TAB}.facetFilter.dateStart`];
  
  if (!dateStart) return oneYearAgo

   // If it's already a Date object, just return it
  if (dateStart instanceof Date) return dateStart

  // Try parsing if it's a string
  const parsedDate = parse(dateStart, DATE_FORMAT, new Date())

  // Return valid parsed date or fallback
  return isValid(parsedDate) ? parsedDate : oneYearAgo
});

const endDate = computed(() => {
  let dateEnd: any = searchStore[`${UPDATES_SEARCH_TAB}.facetFilter.dateEnd`];

  if (!dateEnd) return today

   // If it's already a Date object, just return it
  if (dateEnd instanceof Date) return dateEnd

  // Try parsing if it's a string
  const parsedDate = parse(dateEnd, DATE_FORMAT, new Date())

  // Return valid parsed date or fallback
  return isValid(parsedDate) ? parsedDate : today
});

const onRangeChange = (range: { start: Date | null; end: Date | null }) => {
  searchStore[`${UPDATES_SEARCH_TAB}.facetFilter.dateStart`] = range.start;
  searchStore[`${UPDATES_SEARCH_TAB}.facetFilter.dateEnd`] = range.end;
  if(props.searchOnSelect) {
    searchStore.searchUpdates(true);
  }
};

const updateSelectedItems = (items: SearchFilterOption[]) => {
  if(props.searchOnSelect) {
    searchStore.searchUpdates(true);
  }
}
</script>
<template>
    <div class="updates-filters">
        <div class="filter-item timerange">
            <TimeRangeFilter 
            :iconClose="iconClose"
            :iconOpen="iconOpen"
            :iconMain="iconMain"
            :iconOpenMain="iconOpenMain"
            :startDate="startDate" 
            :endDate="endDate" 
            :autoSelectRange="searchStore[`${UPDATES_SEARCH_TAB}.facetFilter.dateStart`] !== null && searchStore[`${UPDATES_SEARCH_TAB}.facetFilter.dateEnd`] !== null"
            @change="onRangeChange" 
            :rangeLabel="t('custom')" 
            :placeholder="t('timeRange')"></TimeRangeFilter>
        </div>
        <div class="filter-item status">
          <FilterDropdown
            className="accordion"
            :placeholder="t('status')" 
            name="status" 
            :multiple="false" 
            :field="`${UPDATES_SEARCH_TAB}.facetFilter.what`"
            :lookupField="`lookUpOptions.${UPDATES_SEARCH_TAB}.facetFilter.what`"
            :defaultOptionLookupField="`lookUpOptions.${UPDATES_SEARCH_TAB}.what`"
            :hasClear="false" 
            :iconMain="iconStatus"
            :iconOpenMain="iconStatus"
            :searchable="false" 
            @updateSelectedItems="updateSelectedItems"
          />
        </div>
        <div class="filter-item publisher">
          <FilterDropdown 
            className="accordion"
            :placeholder="t('publisher')"
            :searchPlaceholder="t('searchPublisherPlaceholder')"
            name="publisher[]" 
            :multiple="true" 
            :field="`${UPDATES_SEARCH_TAB}.facetFilter.publisher`"
            :lookupField="`lookUpOptions.${UPDATES_SEARCH_TAB}.facetFilter.publisher`"
            :defaultOptionLookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.publisher`"
            :searchable="true"
            :hasClear="false"
            :initialOptions="searchStore['updates.publisher']"
            :iconMain="iconPublisher"
            :iconOpenMain="iconPublisher"
            :showMoreLink="true"
            @updateSelectedItems="updateSelectedItems"
            />
        </div>  
        <div class="filter-item dataset">
          <FilterDropdown
            className="accordion"
            :placeholder="t('dataset')" 
            name="dataset" 
            :multiple="false" 
            :field="`${UPDATES_SEARCH_TAB}.facetFilter.dataset`"
            :lookupField="`lookUpOptions.${UPDATES_SEARCH_TAB}.facetFilter.dataset`"
            :defaultOptionLookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.dataset`"
            :hasClear="false" 
            :iconMain="iconDataset"
            :iconOpenMain="iconDataset"
            :searchable="false" 
            @updateSelectedItems="updateSelectedItems"
            />
        </div>
    </div>
</template>
